import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { readSession, writeSession } from '../core/storage'

/**
 * Een lazy chunk die niet laadt, heeft twee oorzaken:
 * 1. Een haperende verbinding → gewoon opnieuw proberen helpt.
 * 2. Er is halverwege de sessie een nieuwe versie gedeployed → de oude chunk-bestandsnaam
 *    bestaat niet meer (404). Opnieuw proberen helpt nooit; alleen een volledige herlaadbeurt.
 *
 * React.lazy onthoudt een mislukte import voorgoed: "Opnieuw proberen" in een foutgrens
 * gooit dan steeds dezelfde fout. Daarom hier: twee stille herkansingen, en daarna één
 * automatische herlaadbeurt per sessie. Lukt ook dat niet, dan toont de foutgrens een
 * knop "Pagina vernieuwen" (zie isChunkLoadError).
 */

const RETRY_DELAYS_MS = [400, 1200]
const RELOAD_KEY = 'bb-chunk-reload'

export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  return (
    err.name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading (CSS )?chunk \S+ failed/i.test(
      err.message,
    )
  )
}

const wait = (ms: number) => new Promise<void>((resolve) => globalThis.setTimeout(resolve, ms))

async function importWithRetry<T>(factory: () => Promise<T>, name: string): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      const mod = await factory()
      writeSession(RELOAD_KEY, '')
      return mod
    } catch (err) {
      if (!isChunkLoadError(err)) throw err
      const delay = RETRY_DELAYS_MS[attempt]
      if (delay !== undefined) {
        await wait(delay)
        continue
      }
      // Waarschijnlijk een nieuwe deploy: één keer per sessie de pagina herladen.
      if (readSession(RELOAD_KEY) !== name) {
        writeSession(RELOAD_KEY, name)
        window.location.reload()
        // Nooit resolven: de pagina wordt vervangen.
        return new Promise<T>(() => undefined)
      }
      throw err
    }
  }
}

/**
 * Zelfde als React.lazy, maar bestand tegen haperende netwerken en deploys.
 * `lazyWithRetry(() => import('./admin/AdminApp').then((m) => m.AdminApp), 'admin')`
 */
export function lazyWithRetry<P extends object>(
  factory: () => Promise<ComponentType<P>>,
  chunkName: string,
): LazyExoticComponent<ComponentType<P>> {
  return lazy(async () => ({ default: await importWithRetry(factory, chunkName) }))
}
